import { useCallback, useEffect, useState } from 'react'
import { Camera, X } from 'lucide-react'
import {
  GUEST_ALBUM_URL,
  LATEST_UPDATE_PROMO_BODY,
  LATEST_UPDATE_PROMO_CTA,
  LATEST_UPDATE_PROMO_HEADLINE,
  type LatestUpdatePromoSurface,
} from '../content/latest-update-promo'

type LatestUpdatePromoModalProps = {
  /** Re-open whenever this changes between `login` and `home` (each visit to that surface). */
  surface: LatestUpdatePromoSurface
}

export function LatestUpdatePromoModal({ surface }: LatestUpdatePromoModalProps) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!surface) {
      setIsOpen(false)
      return
    }
    setIsOpen(true)
  }, [surface])

  const dismiss = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, dismiss])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        aria-label="Close announcement"
        onClick={dismiss}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="latest-update-promo-title"
        className="relative z-10 w-full max-w-md rounded-xl border border-primary-200/80 bg-white p-6 shadow-2xl sm:p-8"
      >
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 rounded-lg p-2 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-800"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
          <Camera className="h-6 w-6 text-primary-600" aria-hidden />
        </div>

        <h2
          id="latest-update-promo-title"
          className="pr-8 font-serif text-2xl text-primary-900 sm:text-3xl"
        >
          {LATEST_UPDATE_PROMO_HEADLINE}
        </h2>
        <p className="mt-3 text-neutral-600 leading-relaxed">{LATEST_UPDATE_PROMO_BODY}</p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-end">
          <button
            type="button"
            onClick={dismiss}
            className="order-2 rounded-lg border border-neutral-200 px-4 py-3 text-center font-medium text-neutral-700 transition-colors hover:bg-neutral-50 sm:order-1"
          >
            Maybe later
          </button>
          <a
            href={GUEST_ALBUM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            className="order-1 rounded-lg bg-primary-600 px-4 py-3 text-center font-semibold text-white shadow-md transition-colors hover:bg-primary-700 sm:order-2"
          >
            {LATEST_UPDATE_PROMO_CTA}
          </a>
        </div>
      </div>
    </div>
  )
}
